<template>
    <div class="pl-page pl-page--forge">
        <!-- The step's frame. One element for every step; the URL depends on
             both the step and the chosen path, which is the case §5 rule 6
             permits an inline style for. At the crossroads it is the street
             itself, and that step masks its two orders into the ends of it. -->
        <span
            class="pl-backdrop"
            :style="backdrop"
            aria-hidden="true"
        ></span>

        <header class="pl-topbar pl-topbar--forge">
            <div class="pl-lobby-ident">
                <button
                    type="button"
                    class="pl-btn pl-btn--ghost pl-btn--sm"
                    :aria-label="stepIndex === 0 ? t('player.creation.leave') : t('player.creation.back')"
                    @click="goBack"
                >
                    <i class="fas fa-arrow-left" aria-hidden="true"></i>
                </button>

                <div class="pl-topbar__brand">
                    <p class="pl-eyebrow">{{ t('player.creation.eyebrow') }}</p>
                    <p class="pl-title">{{ t(`player.creation.steps.${currentStep.key}`) }}</p>
                </div>
            </div>

            <!-- The road, with the steps as marks on it. The travelled part is
                 drawn in accent; --pl-steps-done is a percentage of the rail
                 and so has to be computed from state (§5 rule 6). -->
            <div v-if="loaded" class="pl-steps-rail">
                <ol class="pl-steps" :style="travelled" :aria-label="t('player.creation.progress')">
                    <li
                        v-for="(step, index) in STEPS"
                        :key="step.key"
                        class="pl-steps__item"
                        :class="{ 'is-on': index === stepIndex, 'is-done': index < stepIndex }"
                        :aria-current="index === stepIndex ? 'step' : undefined"
                    >
                        <button
                            type="button"
                            class="pl-steps__dot"
                            :disabled="!canJumpTo(index)"
                            :title="t(`player.creation.steps.${step.key}`)"
                            :aria-label="t(`player.creation.steps.${step.key}`)"
                            @click="goTo(index)"
                        ></button>
                    </li>
                </ol>

                <p class="pl-steps__count" aria-hidden="true">
                    <span class="pl-steps__at">{{ pad(stepIndex + 1) }}</span>/{{ pad(STEPS.length) }}
                </p>
            </div>
        </header>

        <div v-if="loadState === 'loading'" class="pl-flow__centred">
            <div class="pl-empty" role="status" aria-live="polite">
                <span class="dx-loader dx-loader--sm" aria-hidden="true"></span>
                <p class="pl-empty__hint">{{ t('player.common.loading') }}</p>
            </div>
        </div>

        <div v-else-if="loadState === 'error'" class="pl-flow__centred">
            <div class="pl-empty" role="alert">
                <p class="pl-empty__title">{{ t('player.creation.loadErrorTitle') }}</p>
                <p class="pl-empty__hint">{{ t('player.creation.loadErrorHint') }}</p>
                <button type="button" class="pl-btn pl-btn--ghost" @click="load">
                    {{ t('player.common.retry') }}
                </button>
            </div>
        </div>

        <PathCrossroads
            v-else-if="currentStep.key === 'path'"
            v-model="character.path"
            :paths="paths"
        />

        <div v-else-if="currentStep.key === 'identity'" class="pl-flow__centred">
            <IdentityTerminal
                v-model:name="character.name"
                v-model:age="character.bio.age"
                v-model:gender="character.bio.gender"
                v-model:appearance="character.bio.appearance"
                v-model:background="character.bio.background"
            />
        </div>

        <PortraitPicker
            v-else-if="currentStep.key === 'portrait'"
            v-model="character.bio.avatar_preset"
            :presets="avatarPresets"
            :path="chosenPath"
            :gender="character.bio.gender"
        />

        <ChoiceSlots
            v-else-if="currentStep.key === 'features'"
            v-model="character.modificators"
            :title="t('player.creation.features.title')"
            :hint="t('player.creation.features.hint')"
            :empty-hint="t('player.creation.features.empty')"
            :items="modificators"
            :max="validation.max_modificators_count"
            kind="modificator"
        />

        <ChoiceSlots
            v-else-if="currentStep.key === 'schools'"
            v-model="character.schools"
            :title="t('player.creation.schools.title')"
            :hint="t('player.creation.schools.hint')"
            :empty-hint="t('player.creation.schools.empty')"
            :items="pathSchools"
            :max="validation.max_schools_count"
            kind="school"
            @update:model-value="onSchoolsChanged"
        />

        <ChoiceSlots
            v-else-if="currentStep.key === 'skills'"
            v-model="character.spells"
            :title="t('player.creation.skills.title')"
            :hint="t('player.creation.skills.hint')"
            :empty-hint="t('player.creation.skills.empty')"
            :items="schoolSkills"
            :max="validation.max_spells_count"
            kind="skill"
        />

        <div v-else class="pl-flow__centred">
            <CharacterOath
                :character="character"
                :path="chosenPath"
                :modificators="modificators"
                :schools="schools"
                :skills="skills"
                :avatar-presets="avatarPresets"
                :submitting="submitting"
                :failed="submitFailed"
                @commit="commit"
                @export="exportCharacter"
            />
        </div>

        <!-- The way on. Centred at the foot of the frame rather than in the
             corner of the topbar: it is the one thing a player does on every
             step, and it belongs where they are already looking. Absent until
             the step is answered — a disabled control asks to be clicked and
             then refuses. -->
        <button
            type="button"
            class="pl-forge-go"
            :class="{ 'is-open': canAdvance }"
            :tabindex="canAdvance ? 0 : -1"
            :aria-hidden="!canAdvance"
            @click="goNext"
        >
            {{ t('player.creation.next') }}
            <i class="fas fa-arrow-right" aria-hidden="true"></i>
        </button>
    </div>
</template>

<script setup>
import {computed, nextTick, reactive, ref, watch} from 'vue';
import {useI18n} from 'vue-i18n';
import {useRouter} from 'vue-router';
import {
    CharacterAvatarPresetsGameApi,
    CharacterGameApi,
    ModificatorsGameApi,
    SchoolGameApi,
} from '@/api/backendService.js';
import PathCrossroads from '@/components/Player/Creation/PathCrossroads.vue';
import IdentityTerminal from '@/components/Player/Creation/IdentityTerminal.vue';
import PortraitPicker from '@/components/Player/Creation/PortraitPicker.vue';
import ChoiceSlots from '@/components/Player/Creation/ChoiceSlots.vue';
import CharacterOath from '@/components/Player/Creation/CharacterOath.vue';
import {hasUnusableName} from '@/components/Player/characterPresentation.js';
import {flavourOf} from '@/components/Player/Creation/pathFlavour.js';
import {resolveSelection} from '@/utils/avatarPresets.js';
import {clearDraft, readDraft, writeDraft} from '@/components/Player/Creation/creationDraft.js';

import framePath from '@/assets/images/backgrounds/forge/crossroads-street.webp';
import frameOath from '@/assets/images/backgrounds/forge/oath.webp';
import frameIdentityJson from '@/assets/images/backgrounds/forge/identity-json.webp';
import frameIdentityJohn from '@/assets/images/backgrounds/forge/identity-john.webp';
import framePortraitJson from '@/assets/images/backgrounds/forge/portrait-json.webp';
import framePortraitJohn from '@/assets/images/backgrounds/forge/portrait-john.webp';
import frameFeaturesJson from '@/assets/images/backgrounds/forge/features-json.webp';
import frameFeaturesJohn from '@/assets/images/backgrounds/forge/features-john.webp';
import frameDisciplineJson from '@/assets/images/backgrounds/forge/discipline-json.webp';
import frameDisciplineJohn from '@/assets/images/backgrounds/forge/discipline-john.webp';
import frameSkillsJson from '@/assets/images/backgrounds/forge/skills-json.webp';
import frameSkillsJohn from '@/assets/images/backgrounds/forge/skills-john.webp';

/* One commissioned frame per step. Four of the six differ by path; the
   crossroads and the oath do not — nothing is chosen yet at the first, and both
   orders witness the last. */
const FRAMES = {
    path: {json: framePath, john: framePath},
    identity: {json: frameIdentityJson, john: frameIdentityJohn},
    portrait: {json: framePortraitJson, john: framePortraitJohn},
    features: {json: frameFeaturesJson, john: frameFeaturesJohn},
    schools: {json: frameDisciplineJson, john: frameDisciplineJohn},
    skills: {json: frameSkillsJson, john: frameSkillsJohn},
    oath: {json: frameOath, john: frameOath},
};

/* Path first, and that is a change from the old order.

   Two reasons. The schools step is filtered by path, so asking for a name
   before asking for a path meant the flow's only real dependency ran
   backwards. And the path is the interesting question — it is the one choice
   here that cannot be edited afterwards, so it earns the opening screen
   rather than sitting behind a form. */
const STEPS = [
    {key: 'path'},
    {key: 'identity'},
    {key: 'portrait'},
    {key: 'features'},
    {key: 'schools'},
    {key: 'skills'},
    {key: 'oath'},
];

const {t} = useI18n();
const router = useRouter();

const loadState = ref('loading');
const stepIndex = ref(0);
const submitting = ref(false);
const submitFailed = ref(false);

const paths = ref([]);
const modificators = ref([]);
const schools = ref([]);
const skills = ref([]);
/* Fetched fresh whenever the Portrait step is reached, since the endpoint's
   own filter is (path, gender) — there is no single list to hold across every
   combination the player might pass through. */
const avatarPresets = ref([]);

/* The template's own placeholders are the literal strings "string" and an age
   of 0 (CharacterSubmit seeded them; 04 §2.5.4 records the defect). Starting
   empty means the player sees an empty field and the Oath gate can tell the
   difference between "not filled in" and "filled in badly". */
const character = reactive({
    name: '',
    tags: [],
    bio: {age: null, gender: 'Male', appearance: '', background: '', avatar_preset: null},
    rank: 9,
    path: null,
    stats: [],
    modificators: [],
    items: [],
    schools: [],
    spells: [],
});

const validation = reactive({
    max_modificators_count: 0,
    max_schools_count: 0,
    max_spells_count: 0,
});

const loaded = computed(() => loadState.value === 'ready');

/* How much of the road is behind you, as a percentage of the rail. The marks
   sit at both ends of it, so the travelled line spans (n-1) gaps, not n. */
const travelled = computed(() => ({
    '--pl-steps-done': `${(stepIndex.value / (STEPS.length - 1)) * 100}%`,
}));

/* Two digits, so the counter does not change width as the flow advances. */
function pad(n) {
    return String(n).padStart(2, '0');
}

const currentStep = computed(() => STEPS[stepIndex.value]);
const chosenPath = computed(() => paths.value.find((p) => p.id === character.path) ?? null);

/* Falls back to the flow frame before a path exists, which only matters for the
   crossroads — and there both entries are the same picture anyway. */
const backdrop = computed(() => {
    const frame = FRAMES[currentStep.value.key] ?? FRAMES.path;
    const url = frame[chosenPath.value ? flavourOf(chosenPath.value) : 'json'];
    return {backgroundImage: `url("${url}")`};
});

/* A school belongs to MANY paths — `path` is an m2m and arrives as an array of
   uuids. Accepting the object and scalar forms too costs nothing and keeps
   this working if the serializer is ever depth-expanded. */
function schoolPathIds(school) {
    const raw = school.path;
    if (Array.isArray(raw)) return raw.map((entry) => entry?.id ?? entry);
    if (raw && typeof raw === 'object') return [raw.id];
    return raw ? [raw] : [];
}

/* Of the 23 schools the endpoint returns, only 17 are disciplines a player
   picks. Three filters, and the first is the one that is easy to get backwards:

   - **No path at all means system content, not universal content.** The five
     unpathed schools are "Game Master" (twice), "Weapons", "Artefacts Items"
     and "Runic School of Magic" — inventory and GM plumbing. Offering a school
     because it has no path listed puts the GM's own school in front of a
     player on their first screen.
   - **`is_base` is granted, not chosen.** "Basic" carries both paths and is
     every character's school of life; spending one of a player's two picks on
     it would be a trap.
   - **`game_master_only`** is false on every row today, so it filters nothing.
     It is honoured anyway because it is the field that is *supposed* to mean
     this, and the day it starts being set correctly this should already obey
     it. */
const pathSchools = computed(() => schools.value.filter((school) => {
    if (school.game_master_only || school.is_base) return false;
    const owners = schoolPathIds(school);
    if (!owners.length) return false;
    return owners.includes(character.path);
}));

/* Skill grade runs the opposite way to power: grade 9 is the entry tier and
   grade 0 is the top of a school. A character is created at rank grade 9
   (openai.py:272-278), so it may only take skills at or below its own rank —
   which, on this scale, means a grade greater than or equal to it. */
const schoolSkills = computed(() => {
    if (!character.schools.length) return [];
    return skills.value.filter((skill) => {
        const owner = skill.school?.id ?? skill.school;
        if (!character.schools.includes(owner)) return false;
        return (skill.grade ?? 0) >= character.rank;
    });
});

/* Per step, the one thing that must be true before leaving it means anything.
   Asked of a step by key rather than only of the current one, because the rail
   also has to answer it about steps the player is not standing on. */
function stepSatisfied(key) {
    switch (key) {
        case 'path':
            return !!character.path;
        case 'identity':
            return !hasUnusableName(character.name)
                && Number.isFinite(character.bio.age)
                && character.bio.age >= 18
                && character.bio.age <= 900;
        default:
            return true;
    }
}

/* The Oath step has no Next — it has Commit — so it reports false. */
const canAdvance = computed(() => {
    if (!loaded.value) return false;
    if (currentStep.value.key === 'oath') return false;
    return stepSatisfied(currentStep.value.key);
});

/* How far the flow has been opened. A step is reachable once it has been
   stood on; nothing here re-locks on the way back, so a player who reaches
   the Oath can return to the Portrait and come forward again without
   re-answering the four steps between. */
const furthest = ref(0);

/* Backwards is always allowed — going back to look at a step is not a change,
   and refusing it is what makes a wizard feel like a form. Forwards is allowed
   only as far as the flow has already been opened, AND only while every step
   before the target still answers its own question: clearing the name on the
   way back has to close the steps that were only reachable because it was
   filled in. */
function canJumpTo(index) {
    if (!loaded.value || index === stepIndex.value) return false;
    if (index < stepIndex.value) return true;
    if (index > furthest.value) return false;
    return STEPS.slice(0, index).every((step) => stepSatisfied(step.key));
}

function goTo(index) {
    if (!canJumpTo(index)) return;
    stepIndex.value = index;
    focusStep();
}

watch(stepIndex, (index) => {
    if (index > furthest.value) furthest.value = index;
});

/* Schools gate skills, so dropping a school must drop the skills it granted.
   Leaving them selected would submit skills the character has no school for,
   which the API accepts and the game then cannot explain. */
function onSchoolsChanged(next) {
    const allowed = new Set(
        skills.value
            .filter((skill) => next.includes(skill.school?.id ?? skill.school))
            .map((skill) => skill.id),
    );
    character.spells = character.spells.filter((id) => allowed.has(id));
}

/* The endpoint is filtered server-side by exactly (path, gender), so there is
   no single preset list to hold across the flow — a change to either input
   invalidates the one already fetched. Refetching on every change, rather than
   only when the Portrait step is entered, is what makes "go back, change
   gender, come back" show the new list rather than a stale one a step later. */
async function loadAvatarPresets() {
    if (!character.path || !character.bio.gender) {
        avatarPresets.value = [];
    } else {
        try {
            const response = await CharacterAvatarPresetsGameApi.characterAvatarPresetsList(
                character.bio.gender,
                character.path,
            );
            avatarPresets.value = response.data ?? [];
        } catch {
            avatarPresets.value = [];
        }
    }
    character.bio.avatar_preset = resolveSelection(
        avatarPresets.value,
        character.bio.avatar_preset,
        chosenPath.value,
        character.bio.gender,
    );
}

watch(() => [character.path, character.bio.gender], loadAvatarPresets);

/* Everything the player has answered, kept across a reload.

   Creation is seven steps of typed and chosen answers with no server-side
   record until the Oath is sworn, so until this a refresh — or a mis-click on
   the browser's back button — threw the lot away. The draft is written on
   every change and read back once the reference lists have arrived, which is
   the ordering that matters: the ids in a draft can only be checked against
   the lists they were chosen from. */
function saveDraft() {
    if (!loaded.value) return;
    writeDraft({character, stepIndex: stepIndex.value, furthest: furthest.value});
}

watch([character, stepIndex, furthest], saveDraft, {deep: true});

/* A draft holds uuids, and a uuid is only meaningful against the database it
   was written from — a restored draft can name a path that no longer exists, a
   school the campaign dropped, or skills belonging to a school that is no
   longer selected. Every id is checked against what the flow just loaded and
   dropped if it does not resolve, so a stale draft degrades to a partly filled
   form rather than to a request the API rejects at the very last step.

   The step it puts the player back on is the one they were on, but never
   further than the answers still justify. */
function restoreDraft() {
    const draft = readDraft();
    if (!draft) return;

    const saved = draft.character ?? {};
    const keep = (ids, pool) => {
        const known = new Set(pool.map((row) => row.id));
        return (Array.isArray(ids) ? ids : []).filter((id) => known.has(id));
    };

    character.name = typeof saved.name === 'string' ? saved.name : '';
    character.bio.age = Number.isFinite(saved.bio?.age) ? saved.bio.age : null;
    character.bio.gender = saved.bio?.gender ?? character.bio.gender;
    character.bio.appearance = saved.bio?.appearance ?? '';
    character.bio.background = saved.bio?.background ?? '';
    /* Not restored: the presets are fetched per (path, gender) and the watcher
       below re-resolves the selection against whatever comes back. */
    character.bio.avatar_preset = saved.bio?.avatar_preset ?? null;

    character.path = paths.value.some((row) => row.id === saved.path) ? saved.path : null;
    character.modificators = keep(saved.modificators, modificators.value);
    character.schools = keep(saved.schools, pathSchools.value);
    character.spells = keep(saved.spells, schoolSkills.value);

    const highest = STEPS.reduce(
        (reached, step, index) => (stepSatisfied(step.key) ? Math.max(reached, index + 1) : reached),
        0,
    );
    furthest.value = Math.min(draft.furthest ?? 0, highest, STEPS.length - 1);
    stepIndex.value = Math.min(draft.stepIndex ?? 0, furthest.value);
}

async function load() {
    loadState.value = 'loading';
    try {
        const [template, pathList, modList, schoolList, skillList] = await Promise.all([
            CharacterGameApi.characterPlayerCharacterTemplateRetrieve(),
            SchoolGameApi.schoolPathsGetAllPathsRetrieve(),
            ModificatorsGameApi.modificatorsWorldGetAllModificatorsRetrieve(),
            SchoolGameApi.schoolSchoolsGetAllSchoolsRetrieve(),
            SchoolGameApi.schoolSchoolsGetAllSkillsRetrieve(),
        ]);

        paths.value = pathList.data ?? [];
        modificators.value = modList.data ?? [];
        schools.value = schoolList.data ?? [];
        skills.value = skillList.data ?? [];

        const limits = template.data?.validation ?? {};
        validation.max_modificators_count = limits.max_modificators_count ?? 0;
        validation.max_schools_count = limits.max_schools_count ?? 0;
        validation.max_spells_count = limits.max_spells_count ?? 0;
        /* A non-admin must create at grade 9 (openai.py:272-278). The template
           states it; the constant above is only the fallback. */
        character.rank = template.data?.data?.rank ?? character.rank;

        loadState.value = 'ready';
        restoreDraft();
    } catch {
        loadState.value = 'error';
    }
}

async function focusStep() {
    await nextTick();
    /* Each step's own component focuses its heading on mount. This only has to
       put the window back at the top when a long step is left mid-scroll. */
    window.scrollTo?.({top: 0, behavior: 'auto'});
}

function goNext() {
    if (!canAdvance.value) return;
    if (stepIndex.value < STEPS.length - 1) {
        stepIndex.value += 1;
        focusStep();
    }
}

function goBack() {
    if (stepIndex.value === 0) {
        router.push({name: 'PlayerHomeDashboard'});
        return;
    }
    stepIndex.value -= 1;
    focusStep();
}

function exportCharacter() {
    const blob = new Blob([JSON.stringify(character, null, 2)], {type: 'application/json'});
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${character.name || 'character'}.json`;
    link.click();
    URL.revokeObjectURL(url);
}

async function commit() {
    if (submitting.value) return;
    submitting.value = true;
    submitFailed.value = false;
    try {
        await CharacterGameApi.characterPlayerImportCharacterCreate(character);
        /* The character exists now, so the draft of it is no longer a draft. */
        clearDraft();
        await router.push({name: 'PlayerHomeDashboard'});
    } catch {
        submitFailed.value = true;
        submitting.value = false;
    }
}

load();
</script>
